import React from 'react';
import { CheckCircle, X, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TourCheckoutSummaryProps {
  tourTitle: string;
  tourPrice: number;
  quantity: number;
  tourDate: string;
  orderId: string;
  onClose: () => void;
  onContinueShopping: () => void;
}

export const TourCheckoutSummary: React.FC<TourCheckoutSummaryProps> = ({
  tourTitle,
  tourPrice,
  quantity,
  tourDate,
  orderId,
  onClose,
  onContinueShopping,
}) => {
  const totalPrice = tourPrice * quantity;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-green-600 text-white">
        <div className="flex items-center gap-2 flex-1">
          <CheckCircle className="w-6 h-6" />
          <div>
            <h2 className="font-semibold text-lg">¡Reserva Confirmada!</h2>
            <p className="text-sm text-white/80">Tu excursión está reservada</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800 font-medium">
              ✅ Tu reserva ha sido procesada exitosamente
            </p>
          </div>

          {/* Order Details */}
          <div className="border rounded-lg p-4 space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Excursión
              </h3>
              <p className="text-base font-semibold">{tourTitle}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <div>
                <h4 className="text-xs font-medium text-muted-foreground">Fecha</h4>
                <p className="text-sm font-semibold mt-1">{tourDate}</p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-muted-foreground">Personas</h4>
                <p className="text-sm font-semibold mt-1">{quantity}</p>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Precio por persona:</span>
              <span className="font-medium">ARS ${tourPrice.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Cantidad:</span>
              <span className="font-medium">{quantity}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-3 border-t">
              <span>Total:</span>
              <span className="text-green-600">ARS ${totalPrice.toFixed(0)}</span>
            </div>
          </div>

          {/* Order ID */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-xs font-medium text-blue-800 mb-1">Número de Reserva</div>
            <div className="text-sm font-mono text-blue-900 break-all">{orderId}</div>
          </div>

          {/* Next Steps */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Próximos pasos:</h4>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>✓ Recibirás un email de confirmación</li>
              <li>✓ Tu reserva está asegurada</li>
              <li>✓ Puedes ver tus reservas en tu perfil</li>
            </ul>
          </div>
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-4 space-y-2 bg-muted/20 shrink-0">
        <Button
          onClick={onContinueShopping}
          size="lg"
          className="w-full bg-primary"
        >
          Continuar Explorando
        </Button>
        <Button onClick={onClose} variant="outline" size="lg" className="w-full">
          Cerrar
        </Button>
      </div>
    </div>
  );
};
